import CustomChartTooltip from 'common/components/CustomChartTooltip'
import BaseOptionChart from 'common/components/charts/BaseOptionChart'
import { createArrayOfNumber } from 'common/utils/array'
import { dynamicTickAmount, integerFormatter } from 'common/utils/chart'
import { fNumber } from 'common/utils/formatNumber'
import merge from 'lodash/merge'
import { useGetDeadShrimpLineChart } from 'modules/deadShrimp/api/queries'
import { useGetPartials } from 'modules/food/api/queries'
import { useGetXTitleLineChart, useSpeciesName } from 'modules/pond/api/selector'
import ReactApexChart from 'react-apexcharts'
import { renderToString } from 'react-dom/server'
import { useTranslation } from 'react-i18next'

import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { useShrimpEventAnnotations } from 'hooks/useShrimpEventAnnotations'

const DeadShrimpLineChart = () => {
  const { t } = useTranslation('deadshrimp')
  const speciesName = useSpeciesName()
  const { data, isFetching } = useGetDeadShrimpLineChart()
  const xTitle = useGetXTitleLineChart()
  const { data: partials } = useGetPartials()
  const eventAnnotations = useShrimpEventAnnotations(partials)

  const theme = useTheme()

  const chartData = [
    {
      name: t('dead.shrimp', { speciesName }),
      data: data ? data.map((el) => [el.shrimpAge, el.totalDeadShrimp]) : [],
    },
  ]

  const baseYaxisOption = {
    title: {
      text: t('amount.kg'),
    },
    labels: {
      formatter: (value: string | number) => {
        if (typeof value !== 'undefined') return fNumber(value, { disabledDecimal: true })
        return value
      },
    },
  }

  const chartOptions = merge(BaseOptionChart({ showPartialAnnotation: true }), {
    xaxis: {
      type: 'numeric',
      title: {
        text: xTitle,
      },
      tickAmount: dynamicTickAmount(data?.length || 0),
      labels: {
        formatter: integerFormatter,
      },
    },
    yaxis: baseYaxisOption,
    tooltip: {
      custom: ({
        dataPointIndex,
        series,
        seriesIndex,
        w,
      }: {
        dataPointIndex: number
        series: number[][]
        seriesIndex: number
        w: { globals: { seriesX: number[][] } }
      }) => {
        const accShrimpDead = data?.map((el) => el.accumulatedDeadShrimp) || []
        const tooltipData = [
          {
            title: `${t('dead.shrimp', { speciesName })}:`,
            value: `${fNumber(series[seriesIndex][dataPointIndex])} ${t('kg.unit')}`,
          },
          {
            title: `${t('acc.death.shrimp', { speciesName })}:`,
            value: `${fNumber(accShrimpDead[dataPointIndex])} ${t('kg.unit')}`,
          },
        ]
        return renderToString(
          <CustomChartTooltip
            title={`${t('shrimp.age', { speciesName })}: ${
              w.globals.seriesX[seriesIndex][dataPointIndex]
            } ${t('day')}`}
            data={tooltipData}
          />
        )
      },
    },
    annotations: {
      xaxis: eventAnnotations,
    },
    responsive: [
      {
        breakpoint: theme.breakpoints.values.sm,
        options: {
          legend: {
            position: 'bottom',
          },
          yaxis: {
            title: {
              text: undefined,
            },
            labels: {
              ...baseYaxisOption.labels,
              offsetX: -10,
            },
          },
          xaxis: {
            title: {
              offsetY: -20,
            },
          },
        },
      },
    ],
  })

  if (isFetching)
    return (
      <Stack spacing={3} p={2}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {createArrayOfNumber(3).map((i) => (
            <Skeleton width={60} key={i} />
          ))}
        </Stack>
        {createArrayOfNumber(6).map((i) => (
          <Stack direction="row" key={i} spacing={1}>
            <Skeleton width={40} />
            <Skeleton width="100%" />
          </Stack>
        ))}
        <Stack direction="row" spacing={2}>
          {createArrayOfNumber(6).map((i) => (
            <Skeleton key={i} width="100%" />
          ))}
        </Stack>
      </Stack>
    )

  return <ReactApexChart type="line" series={chartData} options={chartOptions} height={420} />
}

export default DeadShrimpLineChart
